import React, { useState, useRef, useEffect } from 'react';
import { Box, Stepper, Step, StepLabel, Button, Container } from '@mui/material';
import BasicInfoForm from '../../form/BasicInfoForm';
import SelfPromotionForm from '../../form/SelfPromotionForm';
import AchievementForm from '../../form/AchievementForm';
import QualificationForm from '../../form/QualificationForm';
import HumanSkillForm from '../../form/HumanSkillForm';

const steps = ['基本情報', '自己PR', '自己実績', '資格', 'ヒューマンスキル'];

export default function SkillSheetCreateStepper(props) {
  const { skillSheetTemplate, role, csrfToken, formAction } = props;
  const [activeStep, setActiveStep] = useState(0);
  const [formData, setFormData] = useState({
    ...skillSheetTemplate,
    role: role,
    skill_sheet_qualification_infos_attributes: skillSheetTemplate?.skill_sheet_qualification_infos || [],
    validationTriggered: false
  });

  // フラッシュメッセージと初回ログイン時のメッセージを表示するフラグ
  const [showInitialMessages, setShowInitialMessages] = useState(true);

  // ステッパーの参照
  const stepperContainerRef = useRef(null);

  // ステップが変更されたときにスクロール位置を調整する
  useEffect(() => {
    if (stepperContainerRef.current) {
      const stepperContainer = stepperContainerRef.current;
      const stepElements = stepperContainer.querySelectorAll('.MuiStep-root');

      if (stepElements[activeStep]) {
        const activeStepElement = stepElements[activeStep];
        const containerWidth = stepperContainer.offsetWidth;
        const stepWidth = activeStepElement.offsetWidth;
        const stepLeft = activeStepElement.offsetLeft;

        // ステップが中央に来るようにスクロール位置を設定
        const scrollPosition = stepLeft - (containerWidth / 2) + (stepWidth / 2);

        stepperContainer.scrollTo({
          left: scrollPosition,
          behavior: 'smooth'
        });
      }
    }
  }, [activeStep]);

  // バリデーションフラグをリセット
  const resetValidationErrors = () => {
    setFormData(prev => ({ ...prev, validationTriggered: false }));
  };

  // 基本情報のバリデーション
  const validateBasicInfo = () => {
    const nameEmpty = !formData.name || formData.name.trim() === '';
    const birthdayEmpty = !formData.birthday || formData.birthday.trim() === '';
    const nationalityEmpty = !formData.nationality || formData.nationality.trim() === '';

    // 従業員の場合のみ「氏名」も必須
    const fullNameEmpty = formData.role === "employee" 
      ? !formData.full_name || formData.full_name.trim() === ''
      : false;

    if (nameEmpty || birthdayEmpty || nationalityEmpty || fullNameEmpty) {
      setFormData(prev => ({ ...prev, validationTriggered: true }));
      return false;
    }

    return true;
  };

  // 資格情報のバリデーション
  const validateQualificationInfo = () => {
    const qualifications = formData.skill_sheet_qualification_infos_attributes || [];
    const activeQualifications = qualifications.filter(q => !q._destroy);

    const hasNameErrors = activeQualifications.some(q => !q.name || q.name.trim() === '');
    const hasDateErrors = activeQualifications.some(q =>
      (!q.acquisition_date || q.acquisition_date.trim() === '')
    );

    if (hasNameErrors || hasDateErrors) {
      setFormData(prev => ({ ...prev, validationTriggered: true }));
      return false;
    }

    return true;
  };

  // ステップごとのバリデーションをチェック
  const validateCurrentStep = () => {
    // 基本情報の場合、必須項目のチェックを行う
    if (activeStep === 0) {
      return validateBasicInfo();
    }
    // 資格の場合、資格名と取得日の必須チェックを行う
    else if (activeStep === 3) {
      return validateQualificationInfo();
    }

    return true;
  };

  // 次のステップに進む
  const handleNext = () => {
    // バリデーションフラグをセットし、画面の表示を更新
    setFormData(prev => ({ ...prev, validationTriggered: true }));

    // ステップごとのバリデーションが通らない場合は何もしない
    if (!validateCurrentStep()) {
      return;
    }

    // バリデーション成功後、次のステップへ
    setActiveStep((prevActiveStep) => prevActiveStep + 1);
    // 次のステップに進んだらバリデーションフラグをリセット
    resetValidationErrors();
    // メッセージを非表示にする
    setShowInitialMessages(false);
  };

  // 前のステップに戻る
  const handleBack = () => {
    // 前のステップに戻るときはバリデーションをリセット
    resetValidationErrors();
    setActiveStep((prevActiveStep) => prevActiveStep - 1);
  };

  // 特定のステップに移動する
  const handleStepClick = (step) => {
    // 現在と同じステップをクリックした場合は何もしない
    if (step === activeStep) {
      return;
    }

    // 資格ステップから他のステップへ移動する場合、またはステップが進む場合にバリデーションを実行
    if (activeStep === 3 || step > activeStep) {
      setFormData(prev => ({ ...prev, validationTriggered: true }));

      // バリデーションに失敗した場合は移動しない
      if (!validateCurrentStep()) {
        return;
      }
    }

    setActiveStep(step);
    // 移動後にバリデーションフラグをリセット
    resetValidationErrors();
    // メッセージを非表示にする
    setShowInitialMessages(false);
  };

  // 各ステップコンポーネントに渡してフォームデータを更新するハンドラー
  const handleFormChange = (newData) => {
    setFormData((prevData) => {
      // マージする資格を作成
      const mergedData = { ...prevData };

      // 通常のフィールドをマージ
      Object.keys(newData).forEach(key => {
        // 配列の場合（資格情報など）は置き換える
        if (Array.isArray(newData[key]) || key === 'skill_sheet_qualification_infos_attributes') {
          mergedData[key] = newData[key];
        } else {
          mergedData[key] = newData[key];
        }
      });

      return mergedData;
    });

    // バリデーションフラグがfalseの場合はバリデーションフラグをリセット
    if ('validationTriggered' in newData) {
      if (!newData.validationTriggered) {
        // バリデーションをリセット
        resetValidationErrors();
      }
    }
    // バリデーションフラグがtrueの場合はバリデーションを検証
    else if (formData.validationTriggered) {
      // バリデーション表示中の場合は再検証
      validateCurrentStep();
    } else {
      resetValidationErrors();
    }
  };

  // 最終ステップの送信
  const handleSubmit = async (e) => {
    e.preventDefault();

    // バリデーションフラグをtrueにセット
    setFormData(prev => ({ ...prev, validationTriggered: true }));

    // 送信前に最終ステップをバリデーション
    if (!validateCurrentStep()) {
      return;
    }

    // 送信前に空の資格情報をフィルタリングするが、日付データが正しいフォーマットになっていることを確認
    const cleanedFormData = { ...formData };
    if (cleanedFormData.skill_sheet_qualification_infos_attributes) {
      // POSTリクエスト用に資格情報を整理する(削除フラグあり、または有効なデータのみ保持)
      cleanedFormData.skill_sheet_qualification_infos_attributes =
        cleanedFormData.skill_sheet_qualification_infos_attributes
          .map(q => {
            // 削除フラグが立っているものはそのまま返す
            if (q._destroy) return q;
            return q;
          })
          .filter(q => {
            // 削除フラグがある資格は必ず含める
            if (q._destroy) return true;
            // 名前と取得日が両方ある資格だけ含める
            return q.name && q.name.trim() !== '' && q.acquisition_date && q.acquisition_date.trim() !== '';
          });
    }

    // フォーム送信（railsでリダイレクト）
    const form = document.getElementById('skill-sheet-form');
    form.submit();
  };
  
  // ステップごとの入力フォームを表示
  const getStepContent = (step) => {
    switch (step) {
      case 0:
        return <BasicInfoForm formData={formData} onChange={handleFormChange} />;
      case 1:
        return <SelfPromotionForm formData={formData} onChange={handleFormChange} />;
      case 2:
        return <AchievementForm formData={formData} onChange={handleFormChange} />;
      case 3:
        return <QualificationForm formData={formData} onChange={handleFormChange} />;
      case 4:
        return <HumanSkillForm formData={formData} onChange={handleFormChange} />;
      default:
        return '不明なステップ';
    }
  };

  return (
    <Container maxWidth="md">
      <Box sx={{ width: '100%' }}>
        <form id="skill-sheet-form" action={formAction} method="post" onSubmit={handleSubmit}>
          <input type="hidden" name="authenticity_token" value={csrfToken} />

          <Box sx={{
            width: '100%',
            '@media (max-width: 640px)': {
              overflowX: 'scroll',
              '&::-webkit-scrollbar': {
                height: '8px',
              },
              '&::-webkit-scrollbar-track': {
                backgroundColor: '#f1f1f1',
              },
              '&::-webkit-scrollbar-thumb': {
                backgroundColor: '#888',
                borderRadius: '4px',
              },
              '&::-webkit-scrollbar-thumb:hover': {
                backgroundColor: '#555',
              },
            }
          }} ref={stepperContainerRef}>

          {/* 初回ログイン時のメッセージ */}
          {activeStep === 0 && showInitialMessages && (
            <div className="p-4 mb-4 text-sm text-green-700 bg-green-100 rounded-lg" role="alert">
              スキルシートの作成には基本情報の登録が必要です。まずは基本情報を入力してください。
            </div>
          )}

            <Stepper
              activeStep={activeStep}
              nonLinear={true}
              sx={{
                mb: 4,
                '@media (max-width: 640px)': {
                  width: '150%',
                },
                '& .MuiStepIcon-root.Mui-completed': {
                  color: '#4F46E5',
                  '& .MuiStepIcon-completedIcon': {
                    display: 'none'
                  },
                  '& text': {
                    fill: 'white',
                    display: 'block'
                  }
                },
                '& .MuiStepIcon-root.Mui-active': {
                  color: '#4F46E5'
                }
              }}
            >
              {steps.map((label, index) => (
                <Step key={label} completed={false}>
                  <StepLabel
                    onClick={() => handleStepClick(index)}
                    sx={{
                      cursor: 'pointer',
                      '&:hover': {
                        opacity: 0.8
                      },
                      '& .MuiStepIcon-root': {
                        cursor: 'pointer'
                      },
                      '& .MuiStepLabel-label': {
                        cursor: 'pointer',
                        whiteSpace: 'nowrap'
                      }
                    }}
                  >
                    {label}
                  </StepLabel>
                </Step>
              ))}
            </Stepper>
          </Box>

          {activeStep !== steps.length && (
            <Box sx={{ bgcolor: 'white', p: 3, borderRadius: 1 }}>
              {/* ステップごとの入力フォームを表示 */}
              {getStepContent(activeStep)}

              <Box sx={{ display: 'flex', flexDirection: 'row', pt: 2 }}>
                {activeStep !== 0 && (
                  <Button
                    variant="contained"
                    color="error"
                    onClick={handleBack}
                    sx={{ mr: 1 }}
                  >
                    前へ
                  </Button>
                )}
                <Box sx={{ flex: '1 1 auto' }} />
                {activeStep === steps.length - 1 ? (
                  <Button onClick={handleSubmit} variant="contained" sx={{ bgcolor: '#4F46E5', '&:hover': { bgcolor: '#4338CA' } }}>
                    登録
                  </Button>
                ) : (
                  <Button onClick={handleNext} variant="contained" sx={{ bgcolor: '#4F46E5', '&:hover': { bgcolor: '#4338CA' } }}>
                    次へ
                  </Button>
                )}
              </Box>
            </Box>
          )}

          {/* フォームデータをhidden fieldsとして追加 */}
          <input type="hidden" name="skill_sheet_template[name]" value={formData.name || ''} />
          <input type="hidden" name="skill_sheet_template[full_name]" value={formData.full_name || ''} />
          <input type="hidden" name="skill_sheet_template[birthday]" value={formData.birthday || ''} />
          <input type="hidden" name="skill_sheet_template[gender]" value={formData.gender || ''} />
          <input type="hidden" name="skill_sheet_template[nationality]" value={formData.nationality || ''} />
          <input type="hidden" name="skill_sheet_template[nearest_station_line]" value={formData.nearest_station_line || ''} />
          <input type="hidden" name="skill_sheet_template[nearest_station]" value={formData.nearest_station || ''} />
          <input type="hidden" name="skill_sheet_template[final_academic_background]" value={formData.final_academic_background || ''} />
          <input type="hidden" name="skill_sheet_template[self_promotion]" value={formData.self_promotion || ''} />
          <input type="hidden" name="skill_sheet_template[achievement]" value={formData.achievement || ''} />
          <input type="hidden" name="skill_sheet_template[problem_solving]" value={formData.problem_solving || ''} />
          <input type="hidden" name="skill_sheet_template[communication_skill]" value={formData.communication_skill || ''} />
          <input type="hidden" name="skill_sheet_template[autonomy_and_responsibility]" value={formData.autonomy_and_responsibility || ''} />
          <input type="hidden" name="skill_sheet_template[learning_and_growth]" value={formData.learning_and_growth || ''} />
          <input type="hidden" name="skill_sheet_template[team_contribution]" value={formData.team_contribution || ''} />
          {/* 資格情報のhidden input追加 */}
          {formData.skill_sheet_qualification_infos_attributes && formData.skill_sheet_qualification_infos_attributes.map((qual, index) => {
            return (
              <React.Fragment key={index}>
                {/* 削除済みのレコードは_destroyフラグを送信 */}
                {qual._destroy && qual.id && (
                  <>
                    <input
                      type="hidden"
                      name={`skill_sheet_template[skill_sheet_qualification_infos_attributes][${index}][id]`}
                      value={qual.id}
                    />
                    <input
                      type="hidden"
                      name={`skill_sheet_template[skill_sheet_qualification_infos_attributes][${index}][_destroy]`}
                      value="1"
                    />
                  </>
                )}
                {/* 有効なレコードは通常のデータを送信 */}
                {!qual._destroy && (
                  <>
                    <input
                      type="hidden"
                      name={`skill_sheet_template[skill_sheet_qualification_infos_attributes][${index}][name]`}
                      value={qual.name || ''}
                    />
                    <input
                      type="hidden"
                      name={`skill_sheet_template[skill_sheet_qualification_infos_attributes][${index}][acquisition_date]`}
                      value={qual.acquisition_date || ''}
                    />
                    {qual.id && (
                      <input
                        type="hidden"
                        name={`skill_sheet_template[skill_sheet_qualification_infos_attributes][${index}][id]`}
                        value={qual.id}
                      />
                    )}
                  </>
                )}
              </React.Fragment>
            );
          })}
        </form>
      </Box>
    </Container>
  );
} 